import {
  Component,
  forwardRef,
  TemplateRef,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { ControlValueAccessor, Validator, FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { NgbDropdown, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { NgbMonthDatepickerKeyboardService } from './month-date-picker-keyboard.service';

class Idata {
  month: number = 0;
  year: number = 0;
}
@Component({
  selector: 'month-date-picker',
  templateUrl: './month-date-picker.component.html',
  styleUrls: ['./month-date-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthDatePickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: MonthDatePickerComponent,
      multi: true,
    },
  ],
})
export class MonthDatePickerComponent implements ControlValueAccessor {
  data!: Idata;

  dataTxt!: string;
  separator: string;
  monthFirst: boolean;
  place: number;

  isyear: boolean = false;
  _initialYear: number = 0;

  displayMonth: number = new Date().getMonth()+1;
  _displayYear: number = new Date().getFullYear();
  displayYearOld = this.displayYear;

  cellsSelectables: boolean[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((x) => true);

  get displayYear() {
    return this._displayYear;
  }
  set displayYear(value) {
    this._displayYear = Number(value);
    this.setCellsSelectables();
  }

  get initialYear() {
    return this._initialYear;
  }
  set initialYear(value) {
    this._initialYear = value;
    this.setCellsSelectables();
  }

  setValues(initialYear: any, displayYear: any, displayMonth: any) {
    if (this.minDate && initialYear < this.getIncr(this.minDate.year)) return;
    if (this.maxDate && initialYear > this.getIncr(this.maxDate.year) + 11) return;

    if (this.minDate && displayYear < this.minDate.year) return;
    if (this.maxDate && displayYear > this.maxDate.year) return;

    if (this.minDate && displayYear == this.minDate.year && displayMonth < this.minDate.month) {
      if (this.isyear) displayMonth = this.minDate.month;
      else return;
    }

    if (this.maxDate && displayYear == this.maxDate.year && displayMonth > this.maxDate.month) {
      if (this.isyear) displayMonth = this.maxDate.month;
      else return;
    }

    this.initialYear = initialYear;
    this.displayYear = displayYear;
    this.displayMonth = displayMonth;
  }
  changeYearMonth(month: number, year: number, focus: boolean) {
    if (year < this.initialYear) this.initialYear -= 12;

    if (year > this.initialYear + 11) this.initialYear += 12;

    this.displayMonth = month;
    this.displayYear = year;
    if (focus) this.focus();
  }

  get yearId() {
    return this.buttons.find((_, index) => index == 1);
  }
  @ViewChildren('cell')
  cells!: QueryList<ElementRef>;
  @ViewChildren('btn')
  buttons!: QueryList<ElementRef>;

  @ViewChild('template', { static: false })
  templateDef!: TemplateRef<any>;

  @Input() templateData?: TemplateRef<any>;
  months: string[] = [];

  // Allow the input to be disabled, and when it is make it somewhat transparent.
  @Input() disabled = false;
  @Input() mask = 'yyyy-mm';
  @Input() minDate? = new Idata();
  @Input() maxDate? = new Idata();
  @Input() validationError = false;
  @Input() invalidDate: any;

  @ViewChild('calendarPanel')
  calendar!: NgbDropdown;

  onChange!: (data: Idata) => void;
  onTouched = () => {};

  setCellsSelectables() {
    if (this.minDate || this.maxDate) {
      if (this.isyear)
        this.cellsSelectables = this.cellsSelectables.map((_, i) =>
          this.selectable(i + this.initialYear, this.displayMonth),
        );
      else this.cellsSelectables = this.cellsSelectables.map((_, i) => this.selectable(this.displayYear, i + 1));
    }
  }
  private selectable(year: number, month: number): boolean {
    let selectable: boolean = this.minDate
      ? year > this.minDate.year || (year == this.minDate.year && month >= this.minDate.month)
      : true;
    if (selectable && this.maxDate)
      selectable = this.isyear
        ? year <= this.maxDate.year
        : year < this.maxDate.year || (year == this.maxDate.year && month <= this.maxDate.month);

    return selectable;
  }
  constructor(
    private i18n: NgbDatepickerI18n,
    private _keyboardService: NgbMonthDatepickerKeyboardService,
  ) {
    this.separator = this.mask.replace(/m|y|M/gi, '');
    this.monthFirst = this.mask.indexOf('y') > 0;
    this.place = this.mask.indexOf(this.separator);
  }

  ngOnInit() {
    this.months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((x) => this.i18n?.getMonthShortName(x));
  }

  /*Functions that use also keyboard*/
  addYear(incr: number) {
    this.displayYear = this.isyear ? this.displayYear + 12 * incr : this.displayYear + incr;
    this.initialYear = this.getIncr(this.displayYear);
  }

  showYear(show: boolean) {
    this.isyear = show;
    this.setCellsSelectables();
    this.focus();
  }

  selectYearMonth(index: number) {
    if (this.isyear) {
      this.displayYear = index + this.initialYear;
      this.displayYearOld = this.displayYear;
      this.isyear = false;
      this.setCellsSelectables();
      this.focus();
    } else {
      this.displayMonth = index + 1;
      this.data = { year: this.displayYear, month: this.displayMonth };
      this.dataTxt = this.formatData(this.data);
      this.onChange(this.data);
      this.calendar.close();
    }
  }

  /*function open the "calendar"*/
  open() {
    if (this.data && this.data.month && this.data.year) {
      this.displayMonth = this.data.month;
      this.displayYear = this.data.year;
      this.displayYearOld = this.displayYear;
    }
    this.isyear = false;
    this.initialYear = this.getIncr(this.displayYear);
    this.focus();
  }

  focus() {
    setTimeout(() => {
      const cell = this.isyear
        ? this.cells.find((x) => x.nativeElement.dataset.year == this.displayYear)
        : this.cells.find((x) => x.nativeElement.dataset.month == this.displayMonth);
      if (cell) cell.nativeElement.focus();
    });
  }

  onKeyDown(event: KeyboardEvent) {
    this._keyboardService.processKey(event, this);
  }
  change(value: string) {
    value =
      this.separator == ' '
        ? value.replace(/\.|-|\//, ' ')
        : this.separator == '/'
        ? value.replace(/\.|-| /, '/')
        : this.separator == '-'
        ? value.replace(/\.| |\//, '-')
        : value.replace(/.| |\/ /, '-');

    let lastchar = value.substr(value.length - 1);
    if (lastchar == this.separator && value.length <= this.place) {
      if (this.monthFirst) {
        value = '0' + value;
      }
    }
    if (value.length > this.place && value.indexOf(this.separator) < 0) {
      value = value.substr(0, value.length - 1) + this.separator + lastchar;
    }
    this.dataTxt = value;
    this.invalidDate = false;

    if (!value.trim()) {
      this.data = undefined as any;
      this.invalidDate = false;
      this.onChange(this.data);
      return;
    }

    let items = value.split(this.separator);
    if (items.length == 2) {
      let year = (this.monthFirst ? items[1] : items[0]).trim();
      let month = (this.monthFirst ? items[0] : items[1]).trim();
      let imonth = this.months.indexOf(month);
      if (imonth < 0) imonth = parseInt(month, 10);
      else if (imonth >= 13) this.invalidDate = true;
      else imonth = imonth + 1;

      let iyear = parseInt(year, 10);

      // Enforce a 4-digit year: pasted or typed values like 202-01 are invalid.
      if (!/^\d{4}$/.test(year)) {
        this.invalidDate = true;
      }

      if (!Number.isInteger(imonth) || imonth < 1 || imonth > 12) {
        this.invalidDate = true;
      }

      this.data = {
        year: iyear,
        month: imonth,
      };
    } else {
      this.invalidDate = true;
    }
    this.onChange(this.data);
  }

  getIncr(year: number): number {
    return year - (year % 12) - 1;
  }

  formatData(data: Idata): string {
    if (!data) return '';
    let monthTxt = data.month < 10 ? '0' + data.month : '' + data.month;
    return this.monthFirst ? monthTxt + this.separator + data.year : '' + data.year + this.separator + monthTxt;
  }

  writeValue(data: Idata): void {
    this.data = data;
    this.dataTxt = this.formatData(this.data);
  }

  // Allows Angular to register a function to call when the model (rating) changes.
  // Save the function as a property to call later here.
  registerOnChange(fn: (data: Idata) => void): void {
    this.onChange = fn;
  }

  // Allows Angular to register a function to call when the input has been touched.
  // Save the function as a property to call later here.
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Allows Angular to disable the input.
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  //
  validate({ value }: FormControl) {
    if (!value) return null;
    if (this.minDate) {
      if (value.year < this.minDate.year || (value.year == this.minDate.year && value.month < this.minDate.month))
        return { minDate: this.minDate };
    }

    if (this.maxDate) {
      if (value.year > this.maxDate.year || (value.year == this.maxDate.year && value.month > this.maxDate.month))
        return { maxDate: this.maxDate };
    }
    return null;
  }
}
